import { TimeRange } from '../components/TimeRangeNavigation'
import { DeviceInfo } from '../common/enums'

/**
 * Sub-type identifiers for multi-metric sensors (rain/flow)
 */
export type RainfallSubType = 'amount' | 'rate' | 'total'
export type FlowMeterSubType = 'volume' | 'volumeRate' | 'totalVolume'
export type SensorSubType = RainfallSubType | FlowMeterSubType

/**
 * Sensor configuration for export
 */
export interface ExportSensorConfig {
  port: string
  name: string
  productName: string
  unitCategoryId: number
  unitId: number
  unitSymbol: string
  hasData: boolean
  readingCount: number
  subType?: SensorSubType
  parentPort?: string
  isSubRow?: boolean
  subTypeLabel?: string
}

/**
 * Selected sensor for export with unit override
 */
export interface ExportSensorSelection {
  port: string
  selected: boolean
  unitId: number
  unitSymbol: string
  subType?: SensorSubType
}

/**
 * Flow meter sensor metadata (K-Value, Diameter, Area)
 */
export interface FlowMeterMeta {
  kValue?: number
  diameter?: number
  area?: number
  productName?: string
}

/**
 * Export configuration
 */
export interface ExportConfig {
  dateRange: {
    from: number  // Unix timestamp in milliseconds
    to: number    // Unix timestamp in milliseconds
  }
  sensors: ExportSensorSelection[]
  filename: string
  includeHidden: boolean  // Whether to include hidden readings (default: false)
}

/**
 * Metadata for CSV header
 */
export interface ExportMetadata {
  deviceName: string
  sigfoxId: string
  siteName: string
  dateRange: {
    from: Date
    to: Date
  }
  exportedAt: Date
  serialNumber?: number | string
  hardwareVersion?: number | string
  firmwareVersion?: number | string
  wisolId?: string
  latitude?: number
  longitude?: number
  elevation?: number
  sensorMeta?: Record<string, FlowMeterMeta>
}

/**
 * Column definition for CSV
 */
export interface CsvColumn {
  port: string
  sensorName: string
  unitSymbol: string
  header: string  // Format: Port_SensorName_Unit
}

/**
 * Reading value at a specific timestamp for a specific sensor
 */
export interface ReadingValue {
  timestamp: number
  value: number | null
}

/**
 * Sensor readings grouped by port
 */
export interface SensorReadings {
  port: string
  unitCategoryId: number
  unitId: number
  unitSymbol: string
  productName: string
  data: ReadingValue[]
  hidden?: Record<string, boolean>  // timestamp -> hidden flag
}

/**
 * Progress callback for large exports
 */
export interface ExportProgress {
  phase: 'preparing' | 'processing' | 'generating' | 'complete'
  current: number
  total: number
  message: string
}

export type ExportProgressCallback = (progress: ExportProgress) => void

/**
 * Export result
 */
export interface ExportResult {
  success: boolean
  filename?: string
  rowCount?: number
  error?: string
}

/**
 * Props for ExportModal component
 */
export interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  device: DeviceInfo
  currentTimeRange: TimeRange
  sensorConfigs: Record<string, any>
  readingsData?: any
  batteryData?: any
  devices?: Record<string, DeviceInfo>
}

/**
 * State for export modal
 */
export interface ExportModalState {
  dateRange: {
    from: number
    to: number
  }
  selectedSensors: Map<string, ExportSensorSelection>
  filename: string
  isExporting: boolean
  progress: ExportProgress | null
  error: string | null
}

/**
 * Date range preset
 */
export type DateRangePreset = '1d' | '7d' | '28d' | 'custom'

/**
 * Timestamp format options for export
 */
export type TimestampFormat = 'iso' | 'us' | 'eu' | 'unix'

export const TIMESTAMP_FORMATS: { value: TimestampFormat; label: string; example: string }[] = [
  { value: 'iso', label: 'Default', example: '2026-03-04T10:30:00-07:00' },
  { value: 'us', label: 'US (MM/DD/YYYY)', example: '03/04/2026 10:30:00 AM' },
  { value: 'eu', label: 'EU (DD/MM/YYYY)', example: '04/03/2026 10:30:00' },
  { value: 'unix', label: 'Unix Timestamp', example: '1772835000' },
]

/**
 * Status for a single device during multi-device export
 */
export interface DeviceExportStatus {
  sigfoxId: string
  deviceName: string
  status: 'pending' | 'fetching' | 'generating' | 'complete' | 'error'
  error?: string
}

/**
 * Progress tracking for multi-device export
 */
export interface MultiExportProgress {
  totalDevices: number
  completedDevices: number
  currentDeviceId: string | null
  deviceStatuses: Record<string, DeviceExportStatus>
}
